import React from 'react';
import { useDispatch, useSelector } from 'react-redux';

import * as Yup from 'yup';
import { Form, Formik } from 'formik';
import { Box, Grid, Stack } from '@mui/material';
import _ from 'lodash';

import useNotification from '../../../../../core/hooks/use_notification';
import { getAllPodsState } from '../../../../../features/pod/get_all_pods';
import { getAutomaticCouponSettingAction, getAutomaticCouponSettingState } from '../../../../../features/coupon/get_automatic_coupon_setting';
import TextFieldFormik from '../../../../common/text_field';
import RadioGroupFormik from '../../../../common/radio_group';
import { SelectFieldMultipleFormik, SelectFieldMultipleFormikWithMore } from '../../../../common/select_field';
import { SubmitButton } from '../../../../common/button';
import { getAutomaticCouponSettingNotificationData, updateAutomaticCouponSettingNotificationData } from '../../../../../core/utils/notification_utils';
import { ApiRequestStatusEnum } from '../../../../../core/enums/common/api';
import { updateAutomaticCouponSettingAction, updateAutomaticCouponSettingState } from '../../../../../features/coupon/update_automatic_coupon_setting';
import { getAutomaticCouponType } from '../../../../../core/enums/price/automatic_coupon_setting';
import { yesNoOptions } from '../../../../common/form_utils';
import { getAllHubsState } from "../../../../../features/pop/get_all_hubs";
import SectionPaper from '../../../../common/section_paper';
import BottomActionBar from '../../../../common/bottom_action_bar';

const AutomaticCouponSettingForm = ({ automaticCouponType }) => {
    const dispatch = useDispatch();
    const { showNotification, showErrorMessage } = useNotification();

    const updateAutomaticCouponSetting = useSelector(updateAutomaticCouponSettingState);
    const getAutomaticCouponSetting = useSelector(getAutomaticCouponSettingState);
    const getAllPods = useSelector(getAllPodsState);
    const getAllHubs = useSelector(getAllHubsState);

    const initialValues = {
        automaticCouponType: getAutomaticCouponType(getAutomaticCouponSetting?.data?.automaticCouponType)?.message,
        discount: getAutomaticCouponSetting?.data?.discount || '',
        numberOfValidityDays: getAutomaticCouponSetting?.data?.numberOfValidityDays || '',
        allPods: getAutomaticCouponSetting?.data?.allPods || false,
        podIds: getAutomaticCouponSetting?.data?.podIds || [],
        allHubs: getAutomaticCouponSetting?.data?.allHubs || false,
        hubIds: getAutomaticCouponSetting?.data?.hubIds || []
    };

    const schema = Yup.object().shape({
        discount: Yup.number()
            .required()
            .min(0.01)
            .max(100.00),
        numberOfValidityDays: Yup.number()
            .required()
            .min(1),
        allPods: Yup.bool().required(),
        podIds: Yup.array().notRequired(),
        allHubs: Yup.bool().required(),
        hubIds: Yup.array().notRequired(),
    });

    const onSubmit = (values) => {
        const form = _.omit(values, ['automaticCouponType']);

        dispatch(updateAutomaticCouponSettingAction({ form, automaticCouponType }))
            .then(response => {
                showNotification(updateAutomaticCouponSettingNotificationData(response));

                response?.meta?.requestStatus === ApiRequestStatusEnum.FULFILLED && dispatch(getAutomaticCouponSettingAction(automaticCouponType))
                    .then(response => showErrorMessage(getAutomaticCouponSettingNotificationData(response)));
            });
    };

    return (
        <Formik
            initialValues={ initialValues }
            validationSchema={ schema }
            validateOnChange={ false }
            validateOnBlur={ false }
            onSubmit={ onSubmit }>
            { (formik) => (
                <Box component={ Form } sx={ { display: 'flex', flexDirection: 'column', gap: 3, flexGrow: 1 } }>
                    <Box sx={ { flexGrow: 1, pb: 4 } }>
                        <SectionPaper title="Zasady i dystrybucja kuponu">
                            <Grid container spacing={ 3 }>
                                <Grid item xs={ 12 } md={ 6 }>
                                    <TextFieldFormik name="automaticCouponType" label="Typ kuponu" disabled/>
                                </Grid>
                                <Grid item xs={ 12 } md={ 6 }>
                                </Grid>
                                <Grid item xs={ 12 } md={ 6 }>
                                    <TextFieldFormik name="discount" label="Obniżka" type="number" required
                                                     inputProps={ { min: '0.01', max: '100.00', step: '0.01' } }/>
                                </Grid>
                                <Grid item xs={ 12 } md={ 6 }>
                                    <TextFieldFormik name="numberOfValidityDays" label="Ważność kuponu (w dniach)"
                                                     type="number" required inputProps={ { min: '1', step: '1' } }/>
                                </Grid>

                                <Grid item xs={ 12 }>
                                    <Stack spacing={ 3 } sx={ { mt: 2 } }>
                                        <Box>
                                            <RadioGroupFormik
                                                name="allPods"
                                                label="Wszystkie pody"
                                                options={ yesNoOptions }
                                                onChange={ (value) => value === true && formik.setFieldValue('podIds', []) }
                                            />
                                            {
                                                formik.values.allPods === false && (
                                                    <Box sx={ { mt: 2, maxWidth: { xs: '100%', md: '50%' } } }>
                                                        <SelectFieldMultipleFormikWithMore
                                                            name="podIds"
                                                            label="Wybierz pody - wiele"
                                                            defaultOptions={ getAllPods?.data?.filter(pod => pod?.device?.serviceId != null).map(pod => ({
                                                                value: pod?.id,
                                                                label: pod?.indoorPartner?.name + ' (' + pod?.description + ') - ' + pod?.device?.serviceId
                                                            })) }
                                                            extraOptions={ getAllPods?.data?.filter(pod => pod?.device?.serviceId == null).map(pod => ({
                                                                value: pod?.id,
                                                                label: pod?.indoorPartner?.name + ' (' + pod?.description + ') - brak urządzenia'
                                                            })) }
                                                        />
                                                    </Box>
                                                )
                                            }
                                        </Box>

                                        <Box>
                                            <RadioGroupFormik
                                                name="allHubs"
                                                label="Wszystkie huby"
                                                options={ yesNoOptions }
                                                onChange={ (value) => value === true && formik.setFieldValue('hubIds', []) }
                                            />
                                            {
                                                formik.values.allHubs === false && (
                                                    <Box sx={ { mt: 2, maxWidth: { xs: '100%', md: '50%' } } }>
                                                        <SelectFieldMultipleFormik
                                                            name="hubIds"
                                                            label="Wybierz huby - wiele"
                                                            options={ getAllHubs?.data?.map(hub => ({
                                                                value: hub?.id,
                                                                label: hub?.name
                                                            })) }
                                                        />
                                                    </Box>
                                                )
                                            }
                                        </Box>
                                    </Stack>
                                </Grid>
                            </Grid>
                        </SectionPaper>
                    </Box>

                    <BottomActionBar>
                        <Box sx={ { display: 'flex', justifyContent: 'flex-end', width: '100%' } }>
                            <SubmitButton isLoading={ updateAutomaticCouponSetting?.loading }>Zapisz</SubmitButton>
                        </Box>
                    </BottomActionBar>
                </Box>
            ) }
        </Formik>
    );
};

export default AutomaticCouponSettingForm;